
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';

export const useDashboardStats = () => {
  const { toast } = useToast();

  return useQuery({
    queryKey: ['dashboardStats'],
    queryFn: async () => {
      try {
        // Get total authors count
        const { data: authorsData, error: authorsError } = await supabase
          .from('profiles')
          .select('id', { count: 'exact' })
          .eq('role', 'author');

        if (authorsError) throw authorsError;

        // Get total royalties amount
        const { data: royaltiesData, error: royaltiesError } = await supabase
          .from('royalty_reports')
          .select('amount');

        if (royaltiesError) throw royaltiesError;

        const totalRoyalties = royaltiesData?.reduce((sum, report) => sum + Number(report.amount), 0) || 0;

        // Get pending payment requests count
        const { data: pendingPaymentsData, error: pendingPaymentsError } = await supabase
          .from('payment_requests')
          .select('id', { count: 'exact' })
          .eq('status', 'pending');

        if (pendingPaymentsError) throw pendingPaymentsError;

        // Get open tickets count
        const { data: openTicketsData, error: openTicketsError } = await supabase
          .from('support_tickets')
          .select('id', { count: 'exact' })
          .in('status', ['open', 'in_progress']);

        if (openTicketsError) throw openTicketsError;

        // Get quarterly royalty data for trends chart
        const { data: quarterlyData, error: quarterlyError } = await supabase
          .from('royalty_reports')
          .select('quarter, year, amount')
          .order('year', { ascending: true })
          .order('quarter', { ascending: true });

        if (quarterlyError) throw quarterlyError;

        // Group quarterly data
        const quarterlyTrends = quarterlyData?.reduce((acc: any[], report) => {
          const key = `Q${report.quarter} ${report.year}`;
          const existing = acc.find(item => item.name === key);
          
          if (existing) {
            existing.amount += Number(report.amount);
          } else {
            acc.push({
              name: key,
              amount: Number(report.amount)
            });
          }
          
          return acc;
        }, []) || [];

        // Calculate trend percentage (compare latest quarter to previous)
        let trendPercentage = null;
        if (quarterlyTrends.length >= 2) {
          const latest = quarterlyTrends[quarterlyTrends.length - 1].amount;
          const previous = quarterlyTrends[quarterlyTrends.length - 2].amount;
          if (previous > 0) {
            trendPercentage = ((latest - previous) / previous * 100).toFixed(0);
          }
        }

        return {
          totalAuthors: authorsData?.length || 0,
          totalRoyalties,
          pendingPayments: pendingPaymentsData?.length || 0,
          openTickets: openTicketsData?.length || 0,
          quarterlyTrends: quarterlyTrends.slice(-4), // Last 4 quarters
          trendPercentage
        };
      } catch (error: any) {
        toast({
          title: 'Error loading dashboard stats',
          description: error.message,
          variant: 'destructive',
        });
        throw error;
      }
    }
  });
};
